import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { DELAY_BEFORE_ANIMATION_MS } from '#src/components/so5/LastFixtureStory/CardLayout/index.tsx';
import { Title } from '#src/components/so5/LastFixtureStory/LastFixtureStoryStep/Title/index.tsx';
import { LastFixtureStoryStep } from '#src/components/so5/LastFixtureStory/LastFixtureStoryStep/index.tsx';
import type { StoryState } from '#src/components/so5/LastFixtureStory/types.ts';

import { StreakCard } from './StreakCard/index.tsx';
import type {
  CompletedStreakTask_so5Fixture,
  CompletedStreakTask_thresholdsStreakTask,
} from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  padding: var(--triple-unit) 0;
`;

type ContentProps = {
  completedStreakTasks: ComponentProps<
    typeof CompletedStreakTask
  >['completedStreakTasks'];
};
const Content = ({ completedStreakTasks }: ContentProps) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const [alreadyfocusedCard, setAlreadyFocusedCard] = useState<number[]>();

  const itemToDisplayOnDesktop = Math.min(3, completedStreakTasks.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;

  return (
    <div
      style={{
        width: `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`,
      }}
    >
      <Scrollable
        itemToDisplay={itemToDisplay}
        withMask
        onVisibleItemsChanged={items =>
          setAlreadyFocusedCard(prev => [...(prev || []), ...items])
        }
      >
        {completedStreakTasks.map(
          (completedStreakTask, i) =>
            completedStreakTask.completedBy && (
              <Wrapper key={completedStreakTask.id}>
                <StreakCard
                  completedStreakTask={completedStreakTask}
                  focused={alreadyfocusedCard?.includes(i)}
                  delay={
                    i < itemToDisplay ? DELAY_BEFORE_ANIMATION_MS + i * 100 : 0
                  }
                />
              </Wrapper>
            )
        )}
      </Scrollable>
    </div>
  );
};

type Props = {
  from?: StoryState;
  so5Fixture?: CompletedStreakTask_so5Fixture;
  completedStreakTasks: CompletedStreakTask_thresholdsStreakTask[];
  onNext?: ComponentProps<typeof LastFixtureStoryStep>['onNext'];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};
export const CompletedStreakTask = ({
  from,
  so5Fixture,
  completedStreakTasks,
  onNext,
  onClose,
}: Props) => {
  const { intro, tunnel } = useResultsStoryAssets();

  return (
    <LastFixtureStoryStep
      title={<Title type="completedStreakTask" so5Fixture={so5Fixture} />}
      video={{
        asset: from === 'intro' ? intro : tunnel,
        color: '#ea6d29',
        colorDelay: from === 'intro' ? 800 : 0,
      }}
      onNext={onNext}
      onClose={onClose}
    >
      <Content completedStreakTasks={completedStreakTasks} />
    </LastFixtureStoryStep>
  );
};

CompletedStreakTask.fragments = {
  so5Fixture: gql`
    fragment CompletedStreakTask_so5Fixture on So5Fixture {
      slug
      ...Title_so5Fixture
    }
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<CompletedStreakTask_so5Fixture>,
  thresholdsStreakTask: gql`
    fragment CompletedStreakTask_thresholdsStreakTask on ThresholdsStreakTask {
      id
      completedBy {
        id
      }
      ...StreakCard_thresholdsStreakTask
    }
    ${StreakCard.fragments.thresholdsStreakTask}
  ` as TypedDocumentNode<CompletedStreakTask_thresholdsStreakTask>,
};
